import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, CheckCircle2, X } from "lucide-react";

// App-wide toasts for action feedback. Call notifyError / notifySuccess from
// anywhere; <ToastHost /> is mounted once in App.tsx.

type ToastKind = "success" | "error";
interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

let items: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<(items: ToastItem[]) => void>();

function emit() {
  listeners.forEach((l) => l(items));
}

function dismiss(id: number) {
  items = items.filter((t) => t.id !== id);
  emit();
}

function push(kind: ToastKind, message: string) {
  const id = nextId++;
  items = [...items.slice(-3), { id, kind, message }];
  emit();
  setTimeout(() => dismiss(id), kind === "error" ? 7000 : 4000);
}

/**
 * Pull a readable message out of an axios error. Prefers the server's
 * `message`, then a non-code `error` string, then the fallback.
 */
export function errorMessage(err: any, fallback: string): string {
  const data = err?.response?.data;
  if (typeof data?.message === "string" && data.message) return data.message;
  if (typeof data?.error === "string" && data.error && !/^[a-z0-9_]+$/i.test(data.error)) return data.error;
  if (typeof data?.msg === "string" && data.msg) return data.msg;
  if (!err?.response && err?.message === "Network Error") return "Can't reach Kavoa. Check your connection and try again.";
  return fallback;
}

/** Show an error toast. Pass the caught error to use the server's message. */
export function notifyError(fallback: string, err?: any) {
  push("error", err ? errorMessage(err, fallback) : fallback);
}

export function notifySuccess(message: string) {
  push("success", message);
}

export function ToastHost() {
  const [list, setList] = useState<ToastItem[]>(items);

  useEffect(() => {
    listeners.add(setList);
    return () => {
      listeners.delete(setList);
    };
  }, []);

  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[10000] flex flex-col items-center gap-2 w-[calc(100%-2rem)] max-w-md pointer-events-none"
      aria-live="polite"
      role="status"
    >
      <AnimatePresence initial={false}>
        {list.map((t) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.18 }}
            className={`pointer-events-auto w-full flex items-start gap-3 rounded-xl border px-4 py-3 shadow-lg bg-white ${
              t.kind === "error" ? "border-red-200" : "border-emerald-200"
            }`}
          >
            {t.kind === "error" ? (
              <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            ) : (
              <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
            )}
            <p className="flex-1 text-sm text-slate-700">{t.message}</p>
            <button
              onClick={() => dismiss(t.id)}
              className="p-1 -m-1 rounded-lg text-slate-400 hover:text-slate-600"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
